import { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { useHistoryDecisionsV2 } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { ExporterDatasetsEndpoint } from "src/api/endpoints";
import { JobRun } from "src/jobs/types";
import { computeEffectiveHasNextDecisionsPage } from "src/nodeEditor/JobRunIntermediateResultTableWrapper/JobRunIntermediateResultTableWrapper";
import {
  CustomStatusFilterKey,
  computeDecisionsRefetchInterval,
  toDecisionStatusFilter,
} from "src/nodeEditor/JobRunIntermediateResultTableWrapper/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";

export const MAX_LTR_ROW_COUNT = 500;

export const useDataset = (datasetId: string) => {
  const { workspace } = useAuthoringContext();
  return useQuery({
    queryKey: ["minimal_dataset", datasetId],
    queryFn: async () => {
      const response = await ExporterDatasetsEndpoint.getDataset(
        workspace.base_url ?? "",
        datasetId,
        { from: 0, size: MAX_LTR_ROW_COUNT },
      );
      return response;
    },
  });
};

export const useOutputNodeDecisions = ({
  jobRun,
  enabled,
  timeFilter,
  statusFilter,
  expectedResultsCount,
}: {
  jobRun: JobRun;
  enabled: boolean;
  timeFilter: { fromStartTime: string; untilStartTime: string };
  statusFilter: CustomStatusFilterKey;
  expectedResultsCount: number;
}) => {
  const { workspace, flow, version } = useAuthoringContext();
  const [isPolling, setPolling] = useState(enabled);

  const {
    data: decisions,
    isFetching: isFetchingDecisions,
    fetchNextPage: fetchNextDecisionsPage,
    isFetchingNextPage: isFetchingNextDecisionsPage,
    hasNextPage: hasNextDecisionsPage,
  } = useHistoryDecisionsV2({
    baseUrl: workspace.base_url ?? "",
    include_response_data: true,
    include_node_results: true,
    filters: {
      order: "asc",
      fields: {
        flow_version_id: version.id,
        job_id: jobRun.job_id,
        job_run_id: jobRun.id,
        status: toDecisionStatusFilter(statusFilter),
      },
      timeWindow: {
        from: new Date(timeFilter.fromStartTime),
        to: new Date(timeFilter.untilStartTime),
      },
    },
    flowSlug: flow.slug,
    enabled,
    icebergOverride: true,
    keepPreviousData: true,
    refetchInterval: (query) => {
      const { shouldPoll, interval } = computeDecisionsRefetchInterval({
        data: query.state.data,
        totalRowsCount: expectedResultsCount,
      });
      setPolling(shouldPoll);
      return interval;
    },
  });

  const loadedCount =
    decisions?.pages.reduce((acc, page) => acc + page.data.length, 0) ?? 0;

  const effectiveHasNextDecisionsPage = computeEffectiveHasNextDecisionsPage({
    hasNextDecisionsPage,
    expectedResultsCount,
    loadedCount,
  });

  return {
    decisions,
    isFetchingDecisions,
    isFetchingNextDecisionsPage,
    hasNextDecisionsPage: effectiveHasNextDecisionsPage,
    isPollingDecisions: isPolling,
    fetchNextDecisionsPage,
  };
};
